// SmallPopup.js
import React, { useCallback, useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css"; // Import the CSS for react-datepicker
import {
  Popover,
  PopoverContent,
  PopoverBody,
  PopoverFooter,
  Button,
  Input,
  Flex,
} from "@chakra-ui/react";

import { FaFileExcel, FaFileArchive } from "react-icons/fa";
import * as FileSaver from "file-saver";
import XLXS from "sheetjs-style";
import moment from "moment";
import { useDispatch } from "react-redux";
import { useToasts } from "react-toast-notifications";

import { getTdsExcelData } from "./excelData/tdsExcelData";
import { getDirectTaxExcelData } from "./excelData/directTaxExcelData";
import { downloadAndCreateZip } from "./handleDownloadInvoiceZip";
import { getGstExcelData } from "./excelData/gstExcelData";
import { getStartDate } from "jsFunctions/helperFunctions";
import { getEndDate } from "jsFunctions/helperFunctions";
import { fetchAsyncGstXlsxData } from "redux/slices/gstSlice";

import { fetchAsyncTdsXlsxData } from "redux/tdsSlice";
import { fetchAsyncDirectTaxXlsxData } from "redux/directTaxSlice";
import { endOfMonth, startOfMonth } from "rsuite/esm/internals/utils/date";

import SpinnerLoader from "components/Spinner/SpinnerLoader";
import { getExpenseExcelData } from "./excelData/expenseExcelData";
import { fetchAsyncExpenseXlsxData } from "redux/OrganizationExpenseSlice";
import { getSalesInvoiceExcelData } from "./excelData/salesInvoiceExcelData";
import { fetchAsyncSalesInvoiceXlsxData } from "redux/salesInvoice";
import {
  getInvoiceGstExcelData,
  getInvoicePaymentExcelData,
  getInvoiceTDSExcelData,
} from "./excelData/invoiceRecordExcel";
import { fetchAsyncInvoiceGstXlsxData } from "redux/invoicePaymentRecordSlice";
import { fetchAsyncInvoiceTdsXlsxData } from "redux/invoicePaymentRecordSlice";
import { fetchAsyncInvoicePaymentXlsxData } from "redux/invoicePaymentRecordSlice";

const ExportDataModel = ({
  type,
  role,
  filename,
  isOpen,
  handleClose,
  activeBtn,
  moduleId,
}) => {
  const [dates, setDates] = useState([null, null]);

  // const [isOpen, setIsOpen] = useState(false);
  const [expenseData, setExpenseData] = useState([]);
  const [invoiceKeys, setInvoiceKeys] = useState([]);
  const [invoiceSpIds, setInvoiceSpIds] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isMonth, setIsMonth] = useState(false);

  const { addToast } = useToasts();

  const fileType =
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
  const fileExtension = ".xlsx";

  const formatFilename = (filename, dates) => {
    if (dates[0] && dates[1]) {
      return `${filename}${moment(dates[0]).format("DD-MMM-YYYY")}_${moment(dates[1]).format("DD-MMM-YYYY")}`;
    }
    return `${filename}${moment().format("DD-MMM-YYYY")}`;
  };

  const dispatch = useDispatch();

  const fetchDataHandlers = {
    custom: async ({ moduleId, role, startDate, endDate }, dispatch) =>
      (
        await dispatch(
          fetchAsyncExpenseXlsxData({ moduleId, role, startDate, endDate }),
        )
      ).payload?.customExpense || [],
    SalesInvoice: async ({ moduleId, role, startDate, endDate }, dispatch) =>
      (
        await dispatch(
          fetchAsyncSalesInvoiceXlsxData({
            moduleId,
            role,
            startDate,
            endDate,
          }),
        )
      ).payload?.invoiceOrder || [],
    tds: async ({ role, startDate, endDate }, dispatch) =>
      (await dispatch(fetchAsyncTdsXlsxData({ role, startDate, endDate })))
        .payload?.tdsExpense || [],
    directTax: async ({ role, startDate, endDate }, dispatch) =>
      (
        await dispatch(
          fetchAsyncDirectTaxXlsxData({ role, startDate, endDate }),
        )
      ).payload?.directTaxExpense || [],
    gst: async ({ role, startDate, endDate }, dispatch) =>
      (await dispatch(fetchAsyncGstXlsxData({ role, startDate, endDate })))
        .payload?.data || [],
    invoiceGst: async ({ role, startDate, endDate }, dispatch) =>
      (
        await dispatch(
          fetchAsyncInvoiceGstXlsxData({ role, startDate, endDate }),
        )
      ).payload?.gstData || [],
    invoiceTds: async ({ role, startDate, endDate }, dispatch) =>
      (
        await dispatch(
          fetchAsyncInvoiceTdsXlsxData({ role, startDate, endDate }),
        )
      ).payload?.tdsData || [],
    invoicePayment: async ({ role, startDate, endDate }, dispatch) =>
      (
        await dispatch(
          fetchAsyncInvoicePaymentXlsxData({ role, startDate, endDate }),
        )
      ).payload?.paymentData || [],
  };

  const excelDataHandlers = {
    custom: getExpenseExcelData,
    SalesInvoice: getSalesInvoiceExcelData,
    tds: getTdsExcelData,
    directTax: getDirectTaxExcelData,
    gst: getGstExcelData,
    invoiceGst: getInvoiceGstExcelData,
    invoiceTds: getInvoiceTDSExcelData,
    invoicePayment: getInvoicePaymentExcelData,
  };

  const fetchData = useCallback(async () => {
    if (!dates[0] || !dates[1]) return;
    try {
      const startDate = getStartDate(dates[0]);
      const endDate = getEndDate(dates[1]);
      const params = { moduleId, role, startDate, endDate };

      if (fetchDataHandlers[type]) {
        const fetchedData = await fetchDataHandlers[type](params, dispatch);
        setExpenseData(fetchedData);
      }
    } catch (err) {
      addToast(`Error fetching data`, { appearance: "error" });
    }
  }, [dates, type, role, dispatch]);

  const handleDownloadCSV = async () => {
    console.log("calling");

    setIsLoading(true);
    try {
      if (expenseData.length === 0) {
        addToast(`No data available for the selected date range`, {
          appearance: "error",
        });
        return;
      }

      if (excelDataHandlers[type]) {
        const excelData = await excelDataHandlers[type](expenseData);
        const ws = XLXS.utils.json_to_sheet(excelData);
        const columnCount = Object.keys(excelData[0]).length;
        const columnWidths = Array(columnCount).fill({ wch: 15 });
        ws["!cols"] = columnWidths;
        const rowCount = excelData.length;
        const rowHeights = Array(rowCount).fill({ hpt: 20 });
        ws["!rows"] = rowHeights;
        const wb = { Sheets: { data: ws }, SheetNames: ["data"] };
        const excelBuffer = XLXS.write(wb, { bookType: "xlsx", type: "array" });
        const data = new Blob([excelBuffer], { type: fileType });
        FileSaver.saveAs(data, formatFilename(filename, dates) + fileExtension);
        handleClose();
      }
    } catch (error) {
      addToast("An error occurred while downloading the CSV.", {
        appearance: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const extractInvoiceKeys = (data) => {
    const keys = [];
    const ids = [];
    data.forEach((expense) => {
      if (expense.pyInfo && expense.pyInfo?.inv) {
        expense.pyInfo?.inv.forEach((invoice) => {
          if (invoice?.spId) {
            ids.push(invoice?.spId);
          } else {
            if (invoice.Key) {
              keys.push(invoice.Key);
            }
          }
        });
      }
    });

    return { keys, ids };
  };

  const handleDownloadAllInvoices = async () => {
    setIsLoading(true);
    try {
      if (invoiceKeys.length > 0 || invoiceSpIds?.length > 0) {
        const res = await downloadAndCreateZip(
          invoiceKeys,
          formatFilename(filename, dates),
          dispatch,
          invoiceSpIds,
        );
        if (res.success) {
          addToast("Invoices downloaded successfully.", {
            appearance: "success",
          });
        } else {
          addToast("Error downloading invoices.", { appearance: "error" });
        }

        handleClose();
      } else {
        addToast("No invoices found for the selected date range.", {
          appearance: "warning",
        });
      }
    } catch (error) {
      addToast("An error occurred while downloading invoices.", {
        appearance: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (dates[0] !== null && dates[1] !== null) {
      fetchData();
    } else {
      setExpenseData([]);
    }
  }, [dates, fetchData]);

  useEffect(() => {
    if (expenseData && expenseData?.length) {
      const fetchedInvoice = extractInvoiceKeys(expenseData || []);
      const fetchedInvoiceKeys = fetchedInvoice?.keys;
      const fetchedInvoiceIds = fetchedInvoice?.ids;
      setInvoiceKeys(fetchedInvoiceKeys.filter((url) => url));
      setInvoiceSpIds(fetchedInvoiceIds);
    } else {
      setInvoiceKeys([]);
      setInvoiceSpIds([]);
    }
  }, [expenseData]);

  return (
    <Popover
      isOpen={isOpen}
      onClose={handleClose}
      placement="bottom-left"
      className="main-export-popover"
    >
      <PopoverContent width="250px" height="150px" className="export-popover">
        {isLoading ? (
          <SpinnerLoader />
        ) : (
          <>
            <PopoverBody>
              <DatePicker
                selected={dates[0]}
                onChange={(update) => {
                  setDates(update);
                  setIsMonth(true);
                }}
                startDate={dates[0]}
                endDate={dates[1]}
                selectsRange={true}
                isClearable={true}
                customInput={<Input placeholder="Select Date Range" />}
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                onMonthChange={(date) => {
                  if (!isMonth) {
                    const firstDayOfMonth = startOfMonth(date);
                    const lastDayOfMonth = endOfMonth(date);
                    setDates([firstDayOfMonth, lastDayOfMonth]);
                  }
                }}
                placeholderText="Select Date Range"
                className="export-select-date"
              />
            </PopoverBody>
            <PopoverFooter>
              <Flex flexDirection={"column"}>
                {activeBtn === "xlsx" && (
                  <Button
                    colorScheme="blue"
                    className="submit-btn"
                    m={1}
                    leftIcon={<FaFileExcel style={{ color: "#77df77" }} />}
                    onClick={handleDownloadCSV}
                    disabled={!expenseData?.length}
                  >
                    Download XLSX
                  </Button>
                )}
                {type !== "gst" && activeBtn === "invoice" && (
                  <Button
                    colorScheme="blue"
                    className="submit-btn"
                    disabled={!invoiceKeys?.length && !invoiceSpIds?.length}
                    m={3}
                    leftIcon={<FaFileArchive style={{ color: "red" }} />}
                    onClick={handleDownloadAllInvoices}
                  >
                    Download Invoice ZIP
                  </Button>
                )}
              </Flex>
            </PopoverFooter>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default ExportDataModel;
